import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import type { Organization, UserProfile } from '../types';

export function useOrganization() {
  const { user } = useAuth();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.id) {
      loadOrganizationData();
    } else {
      setOrganization(null);
      setUserProfile(null);
      setLoading(false);
    }
  }, [user?.id]);

  const loadOrganizationData = async () => {
    // Ensure user and user.id exist before making queries
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('=== LOADING ORGANIZATION DATA ===');
      console.log('User ID:', user.id);

      // First get user profile
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      console.log('Profile query result:', { data: profileData, error: profileError });

      if (profileError) {
        if (profileError.code === 'PGRST116') {
          // No profile found - this might be a legacy user
          console.log('No profile found for user');
          setUserProfile(null);
          setOrganization(null);
          return;
        }
        throw profileError;
      }

      console.log('Setting user profile:', profileData);
      setUserProfile(profileData);

      // Then get organization data
      if (profileData.organization_id) {
        console.log('Loading organization for ID:', profileData.organization_id);
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('*')
          .eq('id', profileData.organization_id)
          .single();

        console.log('Organization query result:', { data: orgData, error: orgError });

        if (orgError) throw orgError;
        setOrganization(orgData);
      } else {
        console.log('User has no organization');
        setOrganization(null);
      }
    } catch (err: any) {
      console.error('Error loading organization data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const isAdmin = userProfile?.role === 'Admin';
  const canInviteUsers = isAdmin && organization && organization.active_users_count < organization.total_seats;

  return {
    organization,
    userProfile,
    loading,
    error,
    isAdmin,
    canInviteUsers,
    refetch: loadOrganizationData
  };
}